import { useCallback, useEffect, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { faMagnifyingGlass, faRectangleList, faStar } from "@fortawesome/pro-regular-svg-icons"
import { faStar as faStarSolid, faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconButton, Link, Stack, Typography } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { escapeRegExp } from "../../../../../../lib/stringLib"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { StandardButton } from "../../../../buttons/StandardButton"
import { StandardDialog } from "../../../../dialogs/StandardDialog"
import { EmptyContent } from "../../../../skeleton/EmptyContent"
import { SelectedPriceListItem } from "../../../createOrderTypes"
import { FilterSearch } from "../../FilterSearch"

import { MeetingServicePriceListsQuery, savePriceListFavorite } from "./priceListsApi"
import type { MeetingServicePriceListsResponse } from "./priceListsApi"
import type { MeetingServicePriceListsItem } from "./priceListsTypes"
import { MerchantPriceListsGroup } from "./MerchantPriceListsGroup"
import { filterPriceList, restrictToPreSelected, sortPreSelectedFirst } from "./helpers"

const useStyles = makeStyles()((theme) => ({
    // StandardDialog's identity transform only anchors position:fixed children; this dialog has none
    paper: {
        transform: "none",
    },
    // no DialogContent: it would capture the sticky bars as their scrollport, but the paper scrolls
    dialogContent: {
        // the sticky header and footer bleed to the dialog edges, so padding lives on the sections
        color: theme.palette.tailwind.slate[950],
        minHeight: "28rem",
    },
    header: {
        position: "sticky",
        top: 0,
        zIndex: 10,
        padding: "1.5rem 2.5rem 1rem",
        backgroundColor: theme.palette.common.white,
        [theme.breakpoints.down("sm")]: {
            padding: "1rem 1rem .75rem",
        },
    },
    title: {
        fontWeight: 700,
    },
    subtitle: {
        color: theme.palette.tailwind.slate[500],
    },
    closeButton: {
        "&&": {
            width: "2.375rem",
            height: "2.375rem",
            borderRadius: ".6875rem",
            fontSize: "1.0625rem",
            backgroundColor: theme.palette.tailwind.slate[100],
            color: theme.palette.tailwind.slate[600],
        },
        "&&:hover": {
            backgroundColor: theme.palette.tailwind.slate[200],
        },
    },
    body: {
        flexGrow: 1,
        padding: "1.5rem 2.5rem",
        [theme.breakpoints.down("sm")]: {
            padding: "1rem",
        },
    },
    footer: {
        position: "sticky",
        bottom: 0,
        zIndex: 10,
        padding: "1rem 2.5rem",
        backgroundColor: theme.palette.common.white,
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
        [theme.breakpoints.down("sm")]: {
            padding: ".75rem 1rem",
        },
    },
    footerNote: {
        color: theme.palette.tailwind.slate[500],
    },
    // reshapes FilterSearch's outlined field into the flat pill from the design;
    // doubled specificity so these win over the component's own descendant rules
    searchField: {
        // FilterSearch renders its own Box, so the row sizing can only be reached through className
        flexGrow: 1,
        minWidth: 0,
        "&& .MuiOutlinedInput-root": {
            height: "2.875rem",
            borderRadius: ".75rem",
            backgroundColor: theme.palette.tailwind.slate[100],
            boxShadow: "none",
        },
        "&& .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
        "&& .MuiInputAdornment-positionEnd .MuiIconButton-root": {
            borderRadius: ".5625rem",
            padding: ".375rem",
        },
    },
    favoritesToggle: {
        "&&": {
            height: "2.875rem",
            flexShrink: 0,
            borderRadius: ".75rem",
            padding: "0 .75rem",
            borderColor: theme.palette.tailwind.blue[200],
            backgroundColor: theme.palette.tailwind.blue[50],
            color: theme.palette.tailwind.blue[600],
        },
        "&&:hover": {
            backgroundColor: theme.palette.tailwind.blue[100],
        },
        "&&.Mui-disabled": {
            borderColor: theme.palette.tailwind.slate[200],
            backgroundColor: theme.palette.tailwind.slate[100],
            color: theme.palette.tailwind.slate[400],
        },
    },
    favoritesToggleActive: {
        "&&": {
            borderColor: theme.palette.tailwind.blue[600],
            backgroundColor: theme.palette.tailwind.blue[600],
            color: theme.palette.common.white,
        },
        "&&:hover": {
            backgroundColor: theme.palette.tailwind.blue[700],
        },
    },
    favoritesToggleLabel: {
        fontWeight: 700,
        whiteSpace: "nowrap",
    },
    favoritesBadge: {
        minWidth: "1.1875rem",
        height: "1.1875rem",
        padding: "0 .3125rem",
        borderRadius: "62.5rem",
        fontWeight: 800,
        lineHeight: "1.1875rem",
        textAlign: "center",
        backgroundColor: theme.palette.tailwind.blue[600],
        color: theme.palette.common.white,
    },
    favoritesBadgeActive: {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.tailwind.blue[600],
    },
    nearbyBar: {
        padding: ".625rem 1rem",
        borderRadius: ".75rem",
        border: `1px solid ${theme.palette.tailwind.amber[200]}`,
        backgroundColor: theme.palette.tailwind.amber[100],
        color: theme.palette.tailwind.amber[800],
    },
    nearbyLink: {
        fontWeight: 700,
        whiteSpace: "nowrap",
    },
    otherCanteensLabel: {
        paddingTop: ".375rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: ".07em",
        color: theme.palette.tailwind.slate[400],
    },
    noEdge: {
        height: 0,
    },
    emptyWrap: {
        flex: 1,
        justifyContent: "center",
    },
}))
export type SelectPriceListsFiltersTypes = Readonly<{
    showFavoritesOnly: boolean
    search: string
}>

type SelectPriceListsDialogProps = Readonly<{
    userId: number
    onClose: () => void
    merchants: MeetingServicePriceListsItem[]
    preSelectedMerchantIds: ReadonlyArray<number>
    onSelect: (item: SelectedPriceListItem) => void
}>

export function SelectPriceListsDialog({
    onClose,
    merchants,
    preSelectedMerchantIds,
    userId,
    onSelect,
}: SelectPriceListsDialogProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    // the pick is only committed when Continue is pressed, so it stays local until then
    const [selectedItem, setSelectedItem] = useState<SelectedPriceListItem | null>(null)
    const [showAllMerchants, setShowAllMerchants] = useState(false)
    const [filters, setFilters] = useState<SelectPriceListsFiltersTypes>(() => {
        // the star opens on only if the set actually shown on mount has favourites
        const nearby = restrictToPreSelected(merchants, preSelectedMerchantIds)
        const initiallyShown = nearby.length ? nearby : merchants
        const hasFavorites = initiallyShown.some((merchant) => merchant.priceLists.some((pl) => pl.isFavorite))
        return { showFavoritesOnly: hasFavorites, search: "" }
    })

    // the search text is escaped: unescaped input like "(" throws while building the regex
    const searchRegex = useMemo(
        () => (filters.search ? new RegExp(`(${escapeRegExp(filters.search)})`, "i") : undefined),
        [filters.search],
    )

    // a search spans every shown price list; otherwise the star filter silently hides the hits.
    // The nearby restriction below deliberately still applies - "See all canteens" is its way out.
    const isFavoritesFilterActive = filters.showFavoritesOnly && !filters.search

    // an id the merchant list does not carry would otherwise narrow the dialog down to nothing
    const isRestrictable =
        preSelectedMerchantIds.length > 0 &&
        merchants.some((merchant) => preSelectedMerchantIds.includes(merchant.id)) &&
        merchants.some((merchant) => !preSelectedMerchantIds.includes(merchant.id))
    const isRestricted = isRestrictable && !showAllMerchants

    const visibleMerchants = useMemo(() => {
        if (!isRestrictable) return merchants
        return isRestricted
            ? restrictToPreSelected(merchants, preSelectedMerchantIds)
            : sortPreSelectedFirst(merchants, preSelectedMerchantIds)
    }, [merchants, preSelectedMerchantIds, isRestrictable, isRestricted])

    const filteredMerchants = useMemo(
        () => filterPriceList(isFavoritesFilterActive, searchRegex, visibleMerchants),
        [visibleMerchants, searchRegex, isFavoritesFilterActive],
    )

    // resolved after filtering, and only when a nearby merchant actually survived it: otherwise
    // the divider would head a list with nothing above it
    const otherCanteensIndex = useMemo(() => {
        if (!isRestrictable || isRestricted) return -1
        const index = filteredMerchants.findIndex((merchant) => !preSelectedMerchantIds.includes(merchant.id))
        return index > 0 ? index : -1
    }, [filteredMerchants, preSelectedMerchantIds, isRestrictable, isRestricted])

    // counted over what the nearby restriction actually shows, so the toggle cannot filter down
    // to an empty list on a set that has no favourites of its own
    const favoriteItemsCount = useMemo(
        () =>
            visibleMerchants.reduce(
                (count, merchant) => count + merchant.priceLists.filter((pl) => pl.isFavorite).length,
                0,
            ),
        [visibleMerchants],
    )
    useEffect(() => {
        if (favoriteItemsCount === 0) {
            setFilters((prev) => ({ ...prev, showFavoritesOnly: false }))
        }
    }, [favoriteItemsCount])

    const totalPriceListsCount = useMemo(
        () => visibleMerchants.reduce((count, merchant) => count + merchant.priceLists.length, 0),
        [visibleMerchants],
    )

    const queryClient = useQueryClient()

    const { mutate: callSavePriceListFavorite } = useApiMutation(savePriceListFavorite, {})

    const handleFavoriteClick = (merchantId: number, priceListId: number) => {
        queryClient.setQueriesData<MeetingServicePriceListsResponse>(
            { queryKey: [MeetingServicePriceListsQuery.USER_MEETING_SERVICE_PRICE_LISTS] },
            (oldData) => {
                if (!oldData) return oldData
                return {
                    ...oldData,
                    merchants: oldData.merchants.map((m) =>
                        m.id !== merchantId
                            ? m
                            : {
                                  ...m,
                                  priceLists: m.priceLists.map((pl) =>
                                      pl.id !== priceListId ? pl : { ...pl, isFavorite: !pl.isFavorite },
                                  ),
                              },
                    ),
                }
            },
        )

        callSavePriceListFavorite({
            userId,
            merchants: [
                {
                    id: merchantId,
                    priceList: {
                        id: priceListId,
                    },
                },
            ],
        })
    }
    const handleSearch = useCallback((searchText: string) => {
        setFilters((prev) => ({ ...prev, search: searchText }))
    }, [])

    const toggleFavoritesFilter = useCallback(() => {
        setFilters((prev) => ({ ...prev, showFavoritesOnly: !prev.showFavoritesOnly }))
    }, [])

    // "see all" has to mean all: the star filter would otherwise swallow the canteens it just revealed
    const showAllCanteens = useCallback(() => {
        setShowAllMerchants(true)
        setFilters((prev) => ({ ...prev, showFavoritesOnly: false }))
    }, [])

    const showNearbyCanteensOnly = useCallback(() => {
        setShowAllMerchants(false)
    }, [])

    const favoritesToggleLabel = isFavoritesFilterActive
        ? l10n.getString("create-order-select-price-list-favorites-view-all", { count: totalPriceListsCount })
        : l10n.getString("create-order-select-price-list-favorites-section", { count: favoriteItemsCount })

    // onSelect closes the dialog itself, so there is no onClose call here
    const handleContinue = useCallback(() => {
        if (selectedItem) onSelect(selectedItem)
    }, [onSelect, selectedItem])

    const hasAnyPriceLists = totalPriceListsCount > 0

    return (
        <StandardDialog
            open
            onClose={onClose}
            maxWidthProp="lg"
            scroll="paper"
            topbar={<></>}
            updateEdge={classes.noEdge}
            slotProps={{ paper: { className: classes.paper } }}
        >
            <Stack className={classes.dialogContent}>
                <Stack gap="1rem" className={classes.header}>
                    <Stack direction="row" gap="1rem" alignItems="flex-start" justifyContent="space-between">
                        <Stack gap=".125rem">
                            <LocalizedStrict id="create-order-select-price-list-dialog">
                                <Typography variant="h5" className={classes.title}>
                                    Select price list
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict id="create-order-select-price-list-dialog-subtitle">
                                <Typography variant="body2" className={classes.subtitle}>
                                    Meeting catering
                                </Typography>
                            </LocalizedStrict>
                        </Stack>
                        <IconButton
                            onClick={onClose}
                            className={classes.closeButton}
                            title={l10n.getString("create-order-select-price-list-close-button")}
                            aria-label={l10n.getString("create-order-select-price-list-close-button")}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </IconButton>
                    </Stack>
                    <Stack direction="row" gap=".625rem" alignItems="center">
                        <FilterSearch
                            search={filters.search}
                            setSearch={handleSearch}
                            defaultActive
                            activeWidth="100%"
                            className={classes.searchField}
                            placeholderId="create-order-select-price-list-search-placeholder"
                        />
                        {/* the icon and the badge stay outside LocalizedStrict, which replaces children */}
                        <StandardButton
                            variant="outlined"
                            fullWidth={false}
                            className={cx(
                                classes.favoritesToggle,
                                isFavoritesFilterActive && classes.favoritesToggleActive,
                            )}
                            onClick={toggleFavoritesFilter}
                            disabled={favoriteItemsCount === 0 || !!filters.search}
                            aria-pressed={isFavoritesFilterActive}
                            title={favoritesToggleLabel}
                            aria-label={favoritesToggleLabel}
                        >
                            <Stack direction="row" gap=".5rem" alignItems="center">
                                <FontAwesomeIcon icon={isFavoritesFilterActive ? faStarSolid : faStar} />
                                <LocalizedStrict id="create-order-select-price-list-favorites-toggle-label">
                                    <Typography variant="caption" className={classes.favoritesToggleLabel}>
                                        Favorites
                                    </Typography>
                                </LocalizedStrict>
                                {favoriteItemsCount > 0 && (
                                    <Typography
                                        variant="caption"
                                        className={cx(
                                            classes.favoritesBadge,
                                            isFavoritesFilterActive && classes.favoritesBadgeActive,
                                        )}
                                    >
                                        {favoriteItemsCount}
                                    </Typography>
                                )}
                            </Stack>
                        </StandardButton>
                    </Stack>
                </Stack>

                <Stack gap="2rem" className={classes.body}>
                    {/* outside the results branch: an empty search inside the nearby set still needs the way out */}
                    {isRestrictable && (
                        <Stack
                            direction="row"
                            gap="1rem"
                            alignItems="center"
                            justifyContent="space-between"
                            className={classes.nearbyBar}
                        >
                            {isRestricted ? (
                                <>
                                    <LocalizedStrict id="create-order-select-price-list-nearby-note">
                                        <Typography variant="body2">
                                            Showing price lists near your booked room.
                                        </Typography>
                                    </LocalizedStrict>
                                    <LocalizedStrict id="create-order-select-price-list-nearby-see-all-button">
                                        <Link
                                            component="button"
                                            type="button"
                                            color="inherit"
                                            variant="body2"
                                            className={classes.nearbyLink}
                                            onClick={showAllCanteens}
                                        >
                                            See all canteens
                                        </Link>
                                    </LocalizedStrict>
                                </>
                            ) : (
                                <LocalizedStrict id="create-order-select-price-list-nearby-only-button">
                                    <Link
                                        component="button"
                                        type="button"
                                        color="inherit"
                                        variant="body2"
                                        className={classes.nearbyLink}
                                        onClick={showNearbyCanteensOnly}
                                    >
                                        Show only canteens near your booked room
                                    </Link>
                                </LocalizedStrict>
                            )}
                        </Stack>
                    )}
                    {filteredMerchants.length > 0 ? (
                        filteredMerchants.map((merchant, index) => (
                            <Stack gap="1rem" key={merchant.id}>
                                {index === otherCanteensIndex && (
                                    <LocalizedStrict id="create-order-select-price-list-other-canteens-section">
                                        <Typography variant="caption" className={classes.otherCanteensLabel}>
                                            Other canteens
                                        </Typography>
                                    </LocalizedStrict>
                                )}
                                <MerchantPriceListsGroup
                                    merchant={merchant}
                                    onSelect={setSelectedItem}
                                    searchRegex={searchRegex}
                                    selectedItem={selectedItem}
                                    onToggleFavorite={handleFavoriteClick}
                                />
                            </Stack>
                        ))
                    ) : (
                        <Stack className={classes.emptyWrap}>
                            <EmptyContent
                                icon={hasAnyPriceLists ? faMagnifyingGlass : faRectangleList}
                                backgroundColor={tailwindPalette.slate[400]}
                                title={l10n.getString(
                                    hasAnyPriceLists
                                        ? "create-order-select-price-list-no-search-results"
                                        : "create-order-select-price-list-no-price-lists-title",
                                )}
                                description={
                                    hasAnyPriceLists
                                        ? l10n.getString("create-order-select-price-list-no-search-results-description")
                                        : undefined
                                }
                            />
                        </Stack>
                    )}
                </Stack>

                <Stack
                    direction="row"
                    gap="1rem"
                    alignItems="center"
                    justifyContent="space-between"
                    className={classes.footer}
                >
                    {selectedItem ? (
                        <LocalizedStrict
                            id="create-order-select-price-list-footer-selected"
                            vars={{ priceList: selectedItem.priceListName, merchant: selectedItem.merchantName }}
                        >
                            <Typography variant="body2" noWrap className={classes.footerNote}>
                                {selectedItem.priceListName} · {selectedItem.merchantName}
                            </Typography>
                        </LocalizedStrict>
                    ) : (
                        <LocalizedStrict id="create-order-select-price-list-footer-empty">
                            <Typography variant="body2" noWrap className={classes.footerNote}>
                                Choose a price list
                            </Typography>
                        </LocalizedStrict>
                    )}
                    <LocalizedStrict id="create-order-select-price-list-continue-button">
                        <StandardButton
                            variant="contained"
                            fullWidth={false}
                            disabled={!selectedItem}
                            onClick={handleContinue}
                        >
                            Continue
                        </StandardButton>
                    </LocalizedStrict>
                </Stack>
            </Stack>
        </StandardDialog>
    )
}
